import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Zap } from 'lucide-react';
import { services } from '../data/mock';
import { useT } from '../i18n/LanguageContext';

const Services = () => {
  const { t } = useT();

  const section = t.servicesSection || {
    badge: '+ NOTRE EXPERTISE',
    titleA: 'Solutions complètes,',
    titleB: 'de la conception à la livraison.',
    subtitle:
      'Huit métiers complémentaires pour répondre à tous vos besoins en énergie et en bâtiment.',
  };

  const wired = [
    'courants-forts',
    'courants-faibles',
    'energies-renouvelables',
    'mobilite-electrique',
    'renovation-energetique',
    'chauffage-climatisation',
    'vrd',
    'bureau-etudes',
  ];

  return (
    <section
      id="services"
      className="py-20 md:py-24 bg-[#f7f8fb]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* SECTION BADGE */}
        <span
          className="
            inline-block
            px-3
            py-1
            rounded-full
            border
            border-slate-200
            bg-white
            text-slate-600
            text-[11px]
            tracking-[0.25em]
            font-semibold
          "
        >
          {section.badge}
        </span>

        {/* HEADING */}
        <h2
          className="
            mt-6
            max-w-4xl
            text-4xl
            md:text-5xl
            lg:text-[56px]
            font-black
            !text-[#0b1c3a]
            leading-[1.05]
            tracking-tight
          "
        >
          {section.titleA}
          <br />
          {section.titleB}
        </h2>

        {/* DESCRIPTION */}
        <p
          className="
            mt-5
            max-w-3xl
            text-base
            md:text-lg
            !text-slate-600
            leading-relaxed
          "
        >
          {section.subtitle}
        </p>

        {/* SERVICES GRID */}
        <div
          className="
            mt-12
            grid
            sm:grid-cols-2
            lg:grid-cols-4
            gap-6
          "
        >
          {services.map((s) => {
            const hasPage = wired.includes(s.slug);

            const linkTo = hasPage
              ? `/services/${s.slug}`
              : '/services';

            /*
             * Get translated service content.
             * Fallback to mock data so the site does not crash
             * if a translation is temporarily missing.
             */
            const serviceTranslation = t.services?.[s.slug];

            const title = serviceTranslation?.title || s.title;
            const description = serviceTranslation?.desc || s.desc;

            return (
              <Link
                to={linkTo}
                key={s.slug || s.title}
                className="
                  group
                  block
                  bg-white
                  rounded-2xl
                  overflow-hidden
                  border
                  border-slate-200
                  hover:shadow-xl
                  hover:-translate-y-1
                  transition-all
                  duration-300
                "
              >

                {/* IMAGE */}
                <div className="relative aspect-[4/3] overflow-hidden">

                  <img
                    src={s.image}
                    alt={title}
                    className="
                      w-full
                      h-full
                      object-cover
                      group-hover:scale-105
                      transition-transform
                      duration-500
                    "
                  />

                  {/* BLUE ICON */}
                  <div
                    className="
                      absolute
                      top-3
                      left-3
                      w-10
                      h-10
                      rounded-xl
                      bg-[#0b3b8c]
                      text-white
                      flex
                      items-center
                      justify-center
                      shadow-md
                    "
                  >
                    <Zap
                      className="w-5 h-5"
                      strokeWidth={2.5}
                    />
                  </div>

                </div>

                {/* CARD CONTENT */}
                <div className="p-5">

                  {/* TITLE */}
                  <h3
                    className="
                      !text-[#0b1c3a]
                      font-bold
                      text-base
                      md:text-lg
                      leading-tight
                    "
                  >
                    {title}
                  </h3>

                  {/* DESCRIPTION */}
                  <p
                    className="
                      mt-2
                      !text-slate-500
                      text-sm
                      md:text-[15px]
                      leading-relaxed
                      line-clamp-3
                    "
                  >
                    {description}
                  </p>

                  {/* LEARN MORE */}
                  <span
                    className="
                      mt-4
                      inline-flex
                      items-center
                      gap-1.5
                      !text-[#0b3b8c]
                      font-semibold
                      text-sm
                      group-hover:gap-2
                      transition-all
                    "
                  >
                    {t.common.learnMore}

                    <ArrowUpRight
                      className="w-4 h-4"
                    />
                  </span>

                </div>
              </Link>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Services;